// src/app/auth.service.ts
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  Firestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from '@angular/fire/firestore';
import * as bcrypt from 'bcryptjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject = new BehaviorSubject<any>(null);
  user$ = this.userSubject.asObservable();
  private firestore = inject(Firestore);

  constructor() {
    // 🔹 Load stored user on app start
    const storedUser = localStorage.getItem('user');
    if (storedUser) this.userSubject.next(JSON.parse(storedUser));

    // 🔹 Keep localStorage in sync with BehaviorSubject
    this.user$.subscribe((u) => {
      if (u) localStorage.setItem('user', JSON.stringify(u));
      else localStorage.removeItem('user');
    });
  }

  /**
   * ✅ Login with email and password (with bcrypt verification)
   */
  async login(email: string, password: string): Promise<any> {
    try {
      // Fetch employee by email directly from Firestore
      const employeesRef = collection(this.firestore, 'Employees');
      const q = query(
        employeesRef,
        where('email', '==', email.trim().toLowerCase())
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error('Invalid email or password');
      }

      const employeeDoc = snapshot.docs[0];

      // 👇 Explicitly cast as any to avoid TS "password does not exist" error
      const employee: any = { id: employeeDoc.id, ...employeeDoc.data() };

      // Check if password is hashed (bcrypt)
      let isPasswordValid = false;

      if (
        employee.password &&
        (employee.password.startsWith('$2a$') ||
          employee.password.startsWith('$2b$'))
      ) {
        // Hashed password: compare using bcrypt
        isPasswordValid = await bcrypt.compare(password, employee.password);
      } else {
        // Legacy plain text password
        isPasswordValid = password === employee.password;

        // Auto-migrate to hashed password on successful login
        if (isPasswordValid) {
          await this.hashEmployeePassword(employee.id, password);
        }
      }

      if (!isPasswordValid) {
        throw new Error('Invalid email or password');
      }

      // ✅ Set user session
      await this.setUser(employee);
      return employee;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  }

  /**
   * ✅ Login with PIN (for quick access)
   * FIXED: Now validates PIN against the SPECIFIC employee ID
   */
  async loginWithPin(
    pin: string,
    orgId: string,
    employeeId: string
  ): Promise<any> {
    try {
      // Fetch the specific employee by ID
      const employeeRef = doc(this.firestore, 'Employees', employeeId);
      const employeeDoc = await getDoc(employeeRef);

      if (!employeeDoc.exists()) {
        throw new Error('Employee not found');
      }

      const employee: any = { id: employeeDoc.id, ...employeeDoc.data() };

      // Validate that the employee belongs to the selected organization
      if (employee.orgId !== orgId) {
        throw new Error('Invalid employee for this organization');
      }

      // Validate the PIN matches the selected employee's PIN
      if (employee.Pin !== pin) {
        throw new Error('Invalid PIN for this employee');
      }

      await this.setUser(employee);
      return employee;
    } catch (error: any) {
      console.error('PIN login error:', error);
      throw new Error(error.message || 'PIN login failed');
    }
  }

  /**
   * 🔐 Auto-migrate plain text password to hashed
   */
  private async hashEmployeePassword(
    employeeId: string,
    plainPassword: string
  ): Promise<void> {
    try {
      const salt = await bcrypt.genSalt(10);
      const hashedPassword = await bcrypt.hash(plainPassword, salt);

      const employeeRef = doc(this.firestore, 'Employees', employeeId);
      await updateDoc(employeeRef, {
        password: hashedPassword,
        // No plainPassword field - users cannot see their password (industry standard)
      });

      // console.log('✅ Password auto-migrated to hashed format');
    } catch (error) {
      console.error('❌ Failed to hash password:', error);
    }
  }

  /** ✅ When user logs in */
  async setUser(user: any): Promise<void> {
    this.userSubject.next(user);
    await this.recordLoginHistory(user);
  }

  /** ✅ Record login event in Firestore */
  private async recordLoginHistory(user: any): Promise<void> {
    try {
      const historyRef = collection(this.firestore, 'login_history');
      await addDoc(historyRef, {
        userId: user.id || null,
        userName:
          user.Name || user.firstName + ' ' + user.lastName || 'Unknown',
        email: user.email || null,
        orgId: user.orgId || null,
        timestamp: serverTimestamp(),
        device: navigator.userAgent,
        loginMethod: user.loginMethod || 'password', // Track login method
      });
      // console.log(`🕓 Login recorded for ${user.Name || user.email}`);
    } catch (err) {
      console.error('❌ Failed to record login history', err);
    }
  }

  /** ✅ Logout user */
  logout(): void {
    this.clearUser();
    // console.log('👋 User logged out');
  }

  /** ✅ Getters & helpers (restored) */
  getUser(): any {
    // console.log('logged in user', this.userSubject.value);
    return this.userSubject.value;
  }

  getUserName(): string {
    const user = this.userSubject.value;
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.Name || '';
  }

  getUsersOrgId(): string {
    return this.userSubject.value?.orgId || '';
  }

  clearUser(): void {
    this.userSubject.next(null);
  }

  /** ✅ Role-based helpers */
  get role(): string {
    return this.userSubject.value?.role || 'employee';
  }

  get isLoggedIn(): boolean {
    return !!this.userSubject.value;
  }

  get isManager(): boolean {
    return this.role === 'manager';
  }

  get isAdmin(): boolean {
    return this.role === 'admin';
  }
  // In auth.service.ts
  get isSpectator(): boolean {
    const user = this.getUser();
    if (!user) return false;
    return user.role?.toLowerCase() === 'spectator';
  }

  get isEmployee(): boolean {
    const user = this.getUser();
    if (!user) return false;
    return user.role?.toLowerCase() === 'employee';
  }

  /** ✅ Update stored user */
  async updateUser(updatedUser: any): Promise<void> {
    try {
      localStorage.setItem('user', JSON.stringify(updatedUser));
      this.userSubject.next(updatedUser);
      // console.log('✅ User session updated successfully');
    } catch (error) {
      console.error('❌ Error updating user session:', error);
      throw error;
    }
  }

  /**
   * ✅ Verify current password (for password change confirmation)
   */
  async verifyCurrentPassword(currentPassword: string): Promise<boolean> {
    const user = this.getUser();
    if (!user || !user.password) return false;

    try {
      if (user.password.startsWith('$2a$')) {
        // Hashed password
        return await bcrypt.compare(currentPassword, user.password);
      } else {
        // Plain text password
        return currentPassword === user.password;
      }
    } catch (error) {
      console.error('❌ Error verifying password:', error);
      return false;
    }
  }
}
